import styled from "@emotion/styled";
import {
  text,
  nav,
  surface,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  spacing,
} from "@atlas-int/tokens";

// ─── Constants ────────────────────────────────────────────────────────────────

export const sidebarNavItemHoverBackgroundColor = surface.hover;
export const sidebarNavItemBorderLeftWidth = 8; // in pixels
export const sidebarNavItemPadding = "0.625rem";

// ─── Styled primitives ───────────────────────────────────────────────────────

export const SideBarContainer = styled.div`
  flex: 1 15%;
  position: sticky;
  top: 0px;
  align-self: flex-start;
  margin-top: 2rem;
`;

export const SideBarUl = styled.ul`
  letter-spacing: 0.07em;
  font-size: ${fontSize.sm};
  color: ${text.secondary};
  list-style-type: none;
  margin: 0px;
  padding: 0px;

  li {
    line-height: ${lineHeight.tight};
  }
`;

export const InactiveLink = styled.li`
  display: block;
  text-transform: uppercase;
  border-left-width: ${sidebarNavItemBorderLeftWidth}px;
  border-left-style: solid;
  border-left-color: transparent;
  color: ${text.secondary};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.normal};
  letter-spacing: 0.07em;
  padding: ${sidebarNavItemPadding};
  cursor: pointer;

  && a {
    color: inherit;
    text-decoration: none;
  }

  &&:hover {
    background-color: ${sidebarNavItemHoverBackgroundColor};
    text-decoration: none;
  }
`;

export const ActiveLink = styled(InactiveLink)`
  color: ${text.onDark};
  background-color: ${nav.surface};
  border-left-color: ${nav.surface};
  font-weight: ${fontWeight.bold};

  &&:hover {
    background-color: ${nav.surface};
    text-decoration: none;
  }
`;

// ─── SidebarNav component ────────────────────────────────────────────────────

export interface SidebarNavItem {
  id: string;
  label: string;
}

export interface SidebarNavProps {
  /** The id of the currently active item. When undefined, the first item is active. */
  activeItemId?: string;
  /** The list of navigation items to display. */
  items: SidebarNavItem[];
  /** Called when a nav item is clicked. Receives the item id. */
  onItemClick: (id: string) => void;
}

const SidebarNav = ({ activeItemId, items, onItemClick }: SidebarNavProps) => {
  return (
    <SideBarContainer>
      <SideBarUl>
        {items.map((item, i) => {
          const isActive = activeItemId
            ? item.id === activeItemId
            : i === 0;
          const Root = isActive ? ActiveLink : InactiveLink;

          return (
            <Root key={item.id} onClick={() => onItemClick(item.id)}>
              {item.label}
            </Root>
          );
        })}
      </SideBarUl>
    </SideBarContainer>
  );
};

export default SidebarNav;
